import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Controller,
  useForm,
  type FieldError,
  type FieldErrors,
  type Resolver,
  type ResolverResult,
} from 'react-hook-form';
import {
  Button,
  RadioButton,
  Checkbox,
  Input,
  Textarea,
  SubTitleSmall,
  BodyMediumBold,
  BodySmall,
} from '@spaced-out/ui-design-system';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {validateAnswer, type RawAnswer} from './validate';
import {formatAnswers} from 'src/utils/ask-ai';
import type {
  AnswerMap,
  AnswerValue,
  QuestionnaireDraft,
  QuestionnaireWidgetPayload,
  QuestionnaireQuestion,
  QuestionnaireOption,
} from 'src/types/ask-ai';
import css from './questionnaire-widget.css';


export interface QuestionnaireWidgetProps {
  payload: QuestionnaireWidgetPayload;
  /** Called with the formatted Q/A text on final submit. */
  onSubmit: (qaText: string) => void;
  onMinimize: () => void;
  onClose: () => void;
  /** Disable interaction while the server is responding. */
  disabled?: boolean;
  /**
   * Read once at mount to restore wizard progress after a minimize→maximize
   * (which unmounts/remounts this widget). Returns null for a fresh start.
   */
  getInitialDraft?: () => QuestionnaireDraft | null;
  /** Persist wizard progress on every change so it survives a remount. */
  onDraftChange?: (draft: QuestionnaireDraft) => void;
}

/** Single react-hook-form value: every question's answer keyed by id. */
type FormValues = {answers: AnswerMap};

const EMPTY_ANSWER: AnswerValue = {selectedIds: []};

// Skip is hidden from the UI for now (to be rethought); logic kept intact.
const SHOW_SKIP = false;

// Typed field-path builders. `answers.<id>` and its leaves are valid RHF paths
// because FormValues.answers is a Record<string, AnswerValue>.
const nodePath = (id: string) => `answers.${id}` as const;
const selectedPath = (id: string) => `answers.${id}.selectedIds` as const;
const freeformPath = (id: string) => `answers.${id}.freeformText` as const;
const textPath = (id: string) => `answers.${id}.text` as const;

function rawAnswerFor(
  question: QuestionnaireQuestion,
  answer: AnswerValue,
): RawAnswer {
  if (question.question_type === 'text') {
    return answer.text ?? '';
  }
  if (question.question_type === 'single_select') {
    return answer.selectedIds[0] ?? '';
  }
  if (question.question_type === 'multi_select') {
    return answer.selectedIds;
  }
  return undefined; // skip
}

// Seed answer state from each question's optional `default_value` (§6.8).
function buildInitialAnswers(questions: QuestionnaireQuestion[]): AnswerMap {
  const initial: AnswerMap = {};
  for (const q of questions) {
    if (q.default_value == null) {
      continue;
    }
    if (q.question_type === 'text') {
      initial[q.id] = {
        selectedIds: [],
        text: Array.isArray(q.default_value)
          ? q.default_value[0] ?? ''
          : q.default_value,
      };
    } else if (
      q.question_type === 'single_select' ||
      q.question_type === 'multi_select'
    ) {
      initial[q.id] = {
        selectedIds: Array.isArray(q.default_value)
          ? q.default_value
          : [q.default_value],
      };
    }
  }
  return initial;
}

// Reuse the existing per-question rule engine (validate.ts) as an RHF resolver,
// so backend-supplied runtime rules keep working (a static zod schema can't
// express them). Errors are attached at the `answers.<id>` node.
function buildResolver(
  questions: QuestionnaireQuestion[],
): Resolver<FormValues> {
  return (values) => {
    const answerErrors: Record<string, FieldError> = {};
    for (const q of questions) {
      if (q.question_type === 'skip') {
        continue;
      }
      const a = values.answers[q.id] ?? EMPTY_ANSWER;
      if (a.skipped) {
        continue; // explicit skip (only offered on optional questions)
      }
      const message = validateAnswer(q, rawAnswerFor(q, a));
      if (message) {
        answerErrors[q.id] = {type: 'validate', message};
      }
    }
    const hasErrors = Object.keys(answerErrors).length > 0;
    // The errors map is keyed by dynamic question id, which RHF's static
    // FieldErrors type can't express — cast the dynamic node map.
    return {
      values: hasErrors ? ({} as FormValues) : values,
      errors: (hasErrors
        ? {answers: answerErrors}
        : {}) as FieldErrors<FormValues>,
    } satisfies ResolverResult<FormValues>;
  };
}

export function QuestionnaireWidget({
  payload,
  onSubmit,
  onMinimize,
  onClose,
  disabled = false,
  getInitialDraft,
  onDraftChange,
}: QuestionnaireWidgetProps) {
  const {t} = useLabelTranslation();
  const {title, questions} = payload;

  // Read the persisted draft exactly once (it restores progress after a
  // minimize→maximize remount).
  const initialDraftRef = useRef<QuestionnaireDraft | null | undefined>(
    undefined,
  );
  if (initialDraftRef.current === undefined) {
    initialDraftRef.current = getInitialDraft?.() ?? null;
  }
  const initialDraft = initialDraftRef.current;

  // Answers live in react-hook-form; step index and touched-state are wizard UI
  // state persisted alongside the answers.
  const [initialAnswers] = useState<AnswerMap>(
    () => initialDraft?.answers ?? buildInitialAnswers(questions),
  );
  const [index, setIndex] = useState<number>(() => initialDraft?.index ?? 0);
  const [touched, setTouched] = useState<Record<string, boolean>>(
    () => initialDraft?.touched ?? {},
  );

  const resolver = useMemo(() => buildResolver(questions), [questions]);
  const {control, trigger, getValues, setValue, watch, handleSubmit, formState} =
    useForm<FormValues>({
      defaultValues: {answers: initialAnswers},
      resolver,
    });

  const question = questions[index];
  const isLast = index === questions.length - 1;

  // Persist whenever step/touched change; answer edits are persisted by the
  // watch subscription below.
  const persist = useCallback(
    (next: {index: number; touched: Record<string, boolean>}) => {
      onDraftChange?.({
        index: next.index,
        touched: next.touched,
        answers: getValues().answers,
      });
    },
    [onDraftChange, getValues],
  );

  useEffect(() => {
    const sub = watch((value) => {
      onDraftChange?.({
        index,
        touched,
        answers: (value.answers ?? {}) as AnswerMap,
      });
    });
    return () => sub.unsubscribe();
  }, [watch, onDraftChange, index, touched]);

  // Restore error display for questions that were already touched before the
  // remount (RHF clears errors on mount, so re-run validation for them).
  useEffect(() => {
    if (!initialDraft) {
      return;
    }
    const touchedIds = Object.keys(initialDraft.touched).filter(
      (id) => initialDraft.touched[id],
    );
    if (touchedIds.length) {
      void trigger(touchedIds.map(nodePath));
    }
    // Run once on mount to restore errors after a remount.
  }, [initialDraft, trigger]);

  const goToNext = useCallback(async () => {
    if (!question) {
      return;
    }
    const valid = await trigger(nodePath(question.id));
    const nextTouched = {...touched, [question.id]: true};
    const nextIndex = valid
      ? Math.min(index + 1, questions.length - 1)
      : index;
    setTouched(nextTouched);
    if (valid) {
      setIndex(nextIndex);
    }
    persist({index: nextIndex, touched: nextTouched});
  }, [question, trigger, touched, index, questions, persist]);

  const goBack = useCallback(() => {
    const nextIndex = Math.max(0, index - 1);
    setIndex(nextIndex);
    persist({index: nextIndex, touched});
  }, [index, touched, persist]);

  const onValid = useCallback(
    (values: FormValues) => onSubmit(formatAnswers(questions, values.answers)),
    [onSubmit, questions],
  );

  // Submit validates ALL questions: jump to the first invalid one and surface
  // its error (a required question the user never visited must not be omitted).
  const onInvalid = useCallback(
    (errs: FieldErrors<FormValues>) => {
      const answerErrs = errs.answers as
        | Record<string, FieldError | undefined>
        | undefined;
      const nextTouched = {...touched};
      let firstInvalid = -1;
      questions.forEach((q, qi) => {
        if (answerErrs?.[q.id]) {
          nextTouched[q.id] = true;
          if (firstInvalid === -1) {
            firstInvalid = qi;
          }
        }
      });
      const nextIndex = firstInvalid === -1 ? index : firstInvalid;
      setTouched(nextTouched);
      if (firstInvalid !== -1) {
        setIndex(nextIndex);
      }
      persist({index: nextIndex, touched: nextTouched});
    },
    [touched, questions, index, persist],
  );

  const submit = useMemo(
    () => handleSubmit(onValid, onInvalid),
    [handleSubmit, onValid, onInvalid],
  );

  const handleSkip = useCallback(() => {
    if (!question) {
      return;
    }
    setValue(nodePath(question.id), {selectedIds: [], skipped: true});
    if (isLast) {
      onSubmit(formatAnswers(questions, getValues().answers));
      return;
    }
    const nextIndex = Math.min(index + 1, questions.length - 1);
    setIndex(nextIndex);
    persist({index: nextIndex, touched});
  }, [
    question,
    isLast,
    onSubmit,
    questions,
    getValues,
    setValue,
    index,
    touched,
    persist,
  ]);

  if (!question) {
    return null;
  }

  const answerErrors = formState.errors.answers as
    | Record<string, FieldError | undefined>
    | undefined;
  const currentError = answerErrors?.[question.id]?.message;

  return (
    <div className={css.card}>
      <header className={css.header}>
        <SubTitleSmall>{title}</SubTitleSmall>
        <div className={css.headerControls}>
          <Button
            type="ghost"
            iconLeftName="minus"
            size="small"
            onClick={onMinimize}
            aria-label={t('Minimize', 'Minimize')}
          />
          <Button
            type="ghost"
            iconLeftName="close"
            size="small"
            onClick={onClose}
            aria-label={t('Close', 'Close')}
          />
        </div>
      </header>

      {/* key by question id: remounts the field Controllers on navigation so
          RHF's useController re-reads value for the new name (its internal
          useState ignores name changes on a reused instance → stale value
          leaks between consecutive same-type questions). */}
      <div key={question.id} className={css.body}>
        <div
          className={
            question.question_type === 'text'
              ? css.questionTitleFreeform
              : css.questionTitle
          }
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {question.title}
          </ReactMarkdown>
        </div>

        {question.question_type === 'text' && (
          <Controller
            control={control}
            name={textPath(question.id)}
            render={({field}) => (
              <Textarea
                classNames={{textarea: css.textArea}}
                placeholder={question.placeholder}
                value={field.value ?? ''}
                disabled={disabled}
                onChange={(e) => field.onChange(e.currentTarget.value)}
              />
            )}
          />
        )}

        {question.question_type === 'single_select' && (
          <Controller
            control={control}
            name={selectedPath(question.id)}
            render={({field}) => {
              const selectedId = field.value?.[0] ?? '';
              return (
                <div className={css.options}>
                  {(question.options ?? []).map((opt: QuestionnaireOption) =>
                    opt.is_freeform ? (
                      <div key={opt.id} className={css.freeformOption}>
                        <RadioButton
                          name={question.id}
                          value={opt.id}
                          selectedValue={selectedId}
                          disabled={disabled}
                          onChange={(v) => field.onChange([v])}
                          classNames={{wrapper: css.option}}
                          ariaLabel={opt.freeform_placeholder ?? opt.label}
                        />
                        <Controller
                          control={control}
                          name={freeformPath(question.id)}
                          render={({field: freeform}) => (
                            <Input
                              size="small"
                              placeholder={opt.freeform_placeholder}
                              value={freeform.value ?? ''}
                              disabled={disabled}
                              onFocus={() => field.onChange([opt.id])}
                              onChange={(e) =>
                                freeform.onChange(e.currentTarget.value)
                              }
                            />
                          )}
                        />
                      </div>
                    ) : (
                      <RadioButton
                        key={opt.id}
                        name={question.id}
                        value={opt.id}
                        selectedValue={selectedId}
                        disabled={disabled}
                        onChange={(v) => field.onChange([v])}
                        classNames={{wrapper: css.option}}
                      >
                        <span className={css.optionText}>
                          <BodyMediumBold color="primary">
                            {opt.label}
                          </BodyMediumBold>
                          {opt.description ? (
                            <BodySmall color="primary">
                              {opt.description}
                            </BodySmall>
                          ) : null}
                        </span>
                      </RadioButton>
                    ),
                  )}
                </div>
              );
            }}
          />
        )}

        {question.question_type === 'multi_select' && (
          <Controller
            control={control}
            name={selectedPath(question.id)}
            render={({field}) => {
              const selectedIds = field.value ?? [];
              const toggle = (optionId: string, checked: boolean) =>
                field.onChange(
                  checked
                    ? [...selectedIds, optionId]
                    : selectedIds.filter((id) => id !== optionId),
                );
              return (
                <div className={css.options}>
                  {(question.options ?? []).map((opt: QuestionnaireOption) => {
                    const checked = selectedIds.includes(opt.id);
                    return opt.is_freeform ? (
                      <div key={opt.id} className={css.freeformOption}>
                        <Checkbox
                          value={opt.id}
                          checked={checked}
                          disabled={disabled}
                          onChange={({checked: c}) => toggle(opt.id, c)}
                          classNames={{wrapper: css.option}}
                          ariaLabel={opt.freeform_placeholder ?? opt.label}
                        />
                        <Controller
                          control={control}
                          name={freeformPath(question.id)}
                          render={({field: freeform}) => (
                            <Input
                              size="small"
                              placeholder={opt.freeform_placeholder}
                              value={freeform.value ?? ''}
                              disabled={disabled}
                              onFocus={() => {
                                if (!checked) {
                                  toggle(opt.id, true);
                                }
                              }}
                              onChange={(e) =>
                                freeform.onChange(e.currentTarget.value)
                              }
                            />
                          )}
                        />
                      </div>
                    ) : (
                      <Checkbox
                        key={opt.id}
                        value={opt.id}
                        checked={checked}
                        disabled={disabled}
                        onChange={({checked: c}) => toggle(opt.id, c)}
                        classNames={{wrapper: css.option}}
                      >
                        <span className={css.optionText}>
                          <BodyMediumBold color="primary">
                            {opt.label}
                          </BodyMediumBold>
                          {opt.description ? (
                            <BodySmall color="primary">
                              {opt.description}
                            </BodySmall>
                          ) : null}
                        </span>
                      </Checkbox>
                    );
                  })}
                </div>
              );
            }}
          />
        )}

        {currentError && <BodySmall color="danger">{currentError}</BodySmall>}
      </div>

      <footer className={css.footer}>
        <div className={css.footerNav}>
          <Button
            type="ghost"
            size="small"
            iconLeftName="chevron-left"
            onClick={goBack}
            disabled={index === 0 || disabled}
            ariaLabel={t('Back', 'Back')}
          />
          <Button
            type="ghost"
            size="small"
            iconLeftName="chevron-right"
            onClick={goToNext}
            disabled={isLast || disabled}
            ariaLabel={t('Next', 'Next')}
          />
        </div>
        {SHOW_SKIP &&
        (question.question_type === 'skip' || !question.is_required) ? (
          <Button
            type="ghost"
            size="small"
            onClick={handleSkip}
            disabled={disabled}
          >
            {t('Skip', 'Skip')}
          </Button>
        ) : null}
        <Button
          type="secondary"
          size="small"
          onClick={isLast ? submit : goToNext}
          disabled={disabled}
        >
          {isLast ? t('Submit', 'Submit') : t('Next', 'Next')}
        </Button>
      </footer>
    </div>
  );
}
